// formatters.js

// Format codes: e.g. "1234 1234", "12 1234", "abc 12de"
export const formatCode = (value, separator = ' ') => {
  if (!value) return '';

  const clean = value.toString().replace(/[^a-zA-Z0-9]/g, '');
  const reversed = clean.split('').reverse();

  const chunks = [];
  for (let i = 0; i < reversed.length; i += 4) {
    chunks.push(reversed.slice(i, i + 4).join(''));
  }

  return chunks
    .map(chunk => chunk.split('').reverse().join(''))
    .reverse()
    .join(separator);
};

// Format Vietnamese phone numbers: 0908 123 456
export const formatPhone = (value) => {
  if (!value) return '';
  const phone = value.toString().replace(/\D/g, '');
  if (phone.length === 10) {
    // e.g. 0908 123 456
    return `${phone.slice(0, 4)} ${phone.slice(4, 7)} ${phone.slice(7)}`;
  }
  if (phone.length === 11) {
    // e.g. 01234 567 890
    return `${phone.slice(0, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
  }
  return phone; // fallback if length doesn't match expected
};

// Format numbers with commas: 1,234,567.89
export const formatDecimal = (value) => {
  if (value == null || isNaN(Number(value))) return '';
  return Number(value).toLocaleString();
};

// Convert milliliters to liters and format: 1234 => "1.234"
export const formatQuantity = (value) => {
  if (!value) return '';

  const numStr = value.toString().replace(/\D/g, '');
  const num = Number(numStr);

  if (isNaN(num)) return '';

  if (num < 1000) {
    return `${num}`;
  }

  const liters = num / 1000;
  return liters.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

export const calculateTotalFuel = (quantity, price) => {
  const total = Number((quantity * (price * 0.001)).toFixed(0));
  return total.toLocaleString();
};

/** Only use at without Editor */
export const formatDisplayNote = (value) => {
  if(value){
    return value.replace(/<[^>]+>/g, '');
    //return value.replace(/^'|'$/g, '');
  }
  return '';
};

export const inputFormatQuantity = (value) => {
  if (!value) return ''; // Handle empty input

  // Remove any non-numeric characters
  const numStr = value.toString().replace(/[^0-9]/g, '');

  switch (numStr.length) {
    case 1:
      return numStr; // 1 => "1"
    case 2:
      return numStr; // 12 => "12"
    case 3:
      return `${numStr.slice(0, 2)}.${numStr.slice(2)}`; // 120 => "12,0"
    case 4:
      return `${numStr.slice(0, 2)}.${numStr.slice(2)}`; // 1203 => "12,03"
    case 5:
      return `${numStr.slice(0, 2)}.${numStr.slice(2)}`; // 12035 => "12,035"
    default:
      return numStr; // Handle numbers longer than 5 characters (if needed)
  }
};

export const inputFormatCode = (value, onlyNumber = false) => {
  if (!value) return '';
  
  let strTemp = String(value).trim();

  // Remove unwanted characters
  strTemp = onlyNumber
    ? strTemp.replace(/[^0-9]/g, '')
    : strTemp.replace(/\s+/g, '');

  // Insert space every 4 characters
  return strTemp.match(/.{1,4}/g)?.join(' ') || '';
};

/**
 * Remove all thousands separators (like . or ,) from a formatted number string
 * to prepare it for backend (e.g., "1.234.567,89" → "123456789")
 * @param {string} formattedValue - Formatted number string (from input or display)
 * @returns {string} - Plain numeric string without separators
 */
export const sanitizeNumberString = (formattedValue = '') => {
  return formattedValue.replace(/[.,\s]/g, '');
};

/**
 * Remove all whitespace (spaces, tabs, newlines) from a string.
 * @param {string} input - The string to sanitize
 * @returns {string} - Cleaned string with no spaces
 */
export const sanitizeString = (input = '') => {
  return input.replace(/\s+/g, '');
};

/**
 * Compares the new note content from an editor with the original value.
 * If different, sanitizes the new value by removing HTML tags and trimming whitespace.
 * If identical, returns null to indicate no change.
 *
 * @param {string} editorVal - The raw value from the editor (may contain HTML).
 * @param {string} itemVal - The current saved note value (used for comparison).
 * @returns {string|null} - The cleaned note string if changed, otherwise null.
 */
const compareAndSanitizeNote = (editorVal, itemVal) => {
  let noteVal = (editorVal != itemVal) ? editorVal : null;
  if (noteVal) {
    noteVal = noteVal.trim();
    noteVal = noteVal.replace(/^'|'$/g, '');
    // noteVal = noteVal.replace(/<[^>]+>/g, '').trim();
  }
  return noteVal;
};

export const mergeNoteFromEditor = (values, editorRef, currentNote) => {
  const noteEditorValue = editorRef?.current?.getValue?.() || '';
  return {
    ...values,
    note: compareAndSanitizeNote(noteEditorValue, currentNote),
  };
};

/**
 * For live typing
 * Format input string based on format pattern (e.g., 2-2-4) with a custom separator.
 * Example: onChange={(e) => setValue(formatByPattern(e.target.value, [2,2,4], '-'))}
 * @param {string} value - raw input (numbers, letters)
 * @param {number[]} formatPattern - array of group sizes (e.g., [2, 2, 4])
 * @param {string} separator - separator to insert (e.g., '/', '-', '.')
 * @returns {string} - formatted string like 11-12-2025
 */
export const formatInputByPattern = (value, formatPattern = [2, 2, 4], separator = '-') => {
  const raw = value.replace(/\W/g, ''); // Remove non-alphanumeric
  const parts = [];
  let cursor = 0;

  for (let i = 0; i < formatPattern.length && cursor < raw.length; i++) {
    const size = formatPattern[i];
    parts.push(raw.slice(cursor, cursor + size));
    cursor += size;
  }

  // If there's more input beyond defined pattern, continue using last size
  while (cursor < raw.length) {
    const lastSize = formatPattern[formatPattern.length - 1];
    parts.push(raw.slice(cursor, cursor + lastSize));
    cursor += lastSize;
  }

  return parts.join(separator);
};


/**
 * For final submission / display only
 * Format a string for final display based on a group pattern and separator.
 * Example: formatDisplayByPattern("11122025", [2, 2, 4], "/") => "11/12/2025"
 * @param {string} raw - raw string, typically from database or input
 * @param {number[]} pattern - array defining group sizes (e.g., [2,2,4])
 * @param {string} separator - character to insert (e.g., '/', '-', '.')
 * @returns {string} - formatted display string
 */
export const formatDisplayByPattern = (raw, pattern = [2, 2, 4], separator = '/') => {
  const clean = raw?.replace(/\W/g, '') || ''; // keep only alphanumeric
  const parts = [];
  let offset = 0;

  for (let i = 0; i < pattern.length && offset < clean.length; i++) {
    const len = pattern[i];
    const part = clean.slice(offset, offset + len);
    parts.push(part);
    offset += len;
  }

  return parts.join(separator);
};

/**
 * Format number for display with custom separators.
 * @param {number|string} value - Input number or numeric string (e.g. 1234567.89)
 * @param {string} thousandSep - Separator for thousands (e.g. ',', '.')
 * @param {string} decimalSep - Separator for decimals (e.g. '.', ',')
 * @returns {string} - Formatted display string
 */
export const formatDisplayNumber = (value, thousandSep = ',', decimalSep = '.') => {
  if (value == null) return '';

  const str = String(value).replace(/[^0-9.,]/g, '');

  let [integerPart, decimalPart] = str.split(/[.,]/);

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);

  return decimalPart ? `${integerPart}${decimalSep}${decimalPart}` : integerPart;
};



// BACKUP FOR FORMAT QUANTITY
// function formatNumberWithDot(number) {
//   // Calculate the integer part for thousands
//   const integerPart = Math.floor(number / 1000);
//   const remainderPart = number % 1000;

//   let quantity = `${integerPart}`;
//   if (remainderPart > 0) {
//     // Pad the remainder with leading zeros if necessary
//     const paddedRemainder = remainderPart.toString().padStart(3, '0');
//     quantity = `${quantity}.${paddedRemainder}`;
//   }
//   return quantity;
// }
